/**
 * The demo picker has two levels (#2824): industries, then the teams of one
 * industry. This file holds the industry list, the team to industry mapping
 * and the short facts a team card shows. Teams themselves come from
 * `GET /api/demo/scenarios` (see `demoScenarios.ts`).
 */
import type { DemoScenarioCard } from "./demoScenarios";

export type DemoRequirementIcon =
  | "moon"
  | "pin"
  | "calendar"
  | "badge"
  | "shield"
  | "users"
  | "scale"
  | "layers"
  | "gauge"
  | "clock";

export interface DemoRequirement {
  /** One to three words, bold on the need tile. */
  label: string;
  icon: DemoRequirementIcon;
  /** The full sentence, small muted text. */
  text: string;
}

export interface DemoIndustry {
  id: string;
  name: string;
  /** One line for the industry card. */
  tagline: string;
  /** Short blurb on the industry page. */
  blurb: string;
  /** What teams in this industry need. */
  requirements: DemoRequirement[];
}

/**
 * When false, an industry with no available team is hidden from the picker.
 * When true, it shows with a calm "on the way" state and no team cards.
 */
export const showEmptyIndustries = false;

/** Industries kept out of the demo for now (founder, 30 Oct 2026). */
export const HIDDEN_INDUSTRIES: ReadonlySet<string> = new Set(["airline"]);

/** In display order. */
export const DEMO_INDUSTRIES: DemoIndustry[] = [
  {
    id: "hospitals",
    name: "Hospitals",
    tagline: "Night call, many stations and part-time staff.",
    blurb:
      "Hospital teams cover many stations at once. Some stations must always be filled, and night call changes the next day for someone else.",
    requirements: [
      { label: "Night call", icon: "moon", text: "Night call every night, with a rest day after." },
      { label: "Key stations", icon: "pin", text: "Key stations that must always be filled." },
      { label: "Part-time days", icon: "calendar", text: "Part-time staff who work on set days." },
      { label: "Trained stations", icon: "badge", text: "Not every person works every station." },
    ],
  },
  {
    id: "security",
    name: "Security",
    tagline: "A desk that never closes and fair night cover.",
    blurb:
      "Security teams staff a control room and several posts around the clock. The supervisor who covers the night must rest the next day.",
    requirements: [
      {
        label: "Control room",
        icon: "shield",
        text: "A control room staffed day and night, every day.",
      },
      { label: "Lead and officer", icon: "users", text: "A lead and an officer on each shift." },
      { label: "Rest after nights", icon: "moon", text: "A rest day after night cover." },
      {
        label: "Fair night duty",
        icon: "scale",
        text: "Night and standby duty shared fairly within each level.",
      },
    ],
  },
  {
    id: "airline",
    name: "Airline crew",
    tagline: "The right mix of levels on every flight.",
    blurb:
      "Crew bases staff each flight with the right mix of levels. Long flights take their crew out the next day.",
    requirements: [
      { label: "Mix of levels", icon: "layers", text: "One person of each level on every flight." },
      { label: "Rest after flights", icon: "moon", text: "A rest day after a long flight." },
      { label: "Flight limits", icon: "gauge", text: "A limit on long flights in any three days." },
      { label: "Standby cover", icon: "scale", text: "Standby cover that counts toward fairness." },
    ],
  },
  {
    id: "plants",
    name: "Manufacturing plants",
    tagline: "Three shifts a day with licensed operators.",
    blurb:
      "Manufacturing plant teams run three shifts a day. Each shift needs a lead, a technician and a licensed operator.",
    requirements: [
      {
        label: "Three shifts",
        icon: "clock",
        text: "Morning, afternoon and night shifts every day.",
      },
      {
        label: "Lead and tech",
        icon: "users",
        text: "A shift lead and a technician on each shift.",
      },
      { label: "Licensed driver", icon: "badge", text: "A licensed driver on every shift." },
      { label: "Rest after nights", icon: "moon", text: "A rest day after every night." },
    ],
  },
  {
    id: "clinics",
    name: "Clinics",
    tagline: "Weekday sessions, part-timers and cover for absences.",
    blurb:
      "Clinic teams run morning and afternoon sessions on weekdays, with a Saturday morning clinic. Part-time staff work set days, and every absence leaves a session without its doctor or nurse.",
    requirements: [
      {
        label: "Doctor and nurse",
        icon: "users",
        text: "A doctor and a nurse in each clinic session.",
      },
      { label: "Senior doctor", icon: "badge", text: "A senior doctor for the main clinic." },
      { label: "Part-time days", icon: "calendar", text: "Part-time staff on set weekdays." },
      {
        label: "Absence cover",
        icon: "pin",
        text: "Cover for a clinic when its usual holder is away.",
      },
    ],
  },
  {
    id: "kitchens",
    name: "Restaurants",
    tagline: "Split shifts, a closed day and trained cooks.",
    blurb:
      "Restaurant kitchens work lunch and dinner services and close one day a week. Each service needs a senior person on the pass.",
    requirements: [
      {
        label: "Senior on pass",
        icon: "badge",
        text: "A senior person on the pass at every service.",
      },
      { label: "Trained cooks", icon: "badge", text: "Trained cooks on the grill and the wok." },
      { label: "Fair doubles", icon: "scale", text: "A fair share of double shifts." },
      { label: "Leave limits", icon: "calendar", text: "Leave for one senior person at a time." },
    ],
  },
  {
    id: "call-centres",
    name: "Call centres",
    tagline: "Early, day and late shifts, and a queue for trained agents.",
    blurb:
      "Call centres run early, day and late shifts every day of the week. Each shift needs a team lead, and some queues only trained agents can staff.",
    requirements: [
      { label: "Three shifts", icon: "clock", text: "Early, day and late shifts every day." },
      { label: "Team lead", icon: "users", text: "A team lead on every shift." },
      { label: "Enough agents", icon: "gauge", text: "Enough agents on each shift." },
      { label: "Trained queue", icon: "badge", text: "A queue only trained agents can staff." },
    ],
  },
];

/** Team (scenario name) to industry id. Only a fallback: the API sends `industry`. */
const TEAM_INDUSTRY: Record<string, string> = {
  meridian_ed_showcase: "hospitals",
  harbourline_security_small: "security",
  harbourline_security_campus: "security",
  larkspur_ward7_nursing: "hospitals",
  airline_cabin_crew_base: "airline",
  northgate_packaging_line2: "plants",
  saffron_lane_kitchen: "kitchens",
  bayview_family_clinics: "clinics",
  kallang_contact_centre: "call-centres",
};

/** Short facts that show how one team differs from the others in its industry. */
export interface DemoTeamFacts {
  hours: string;
  hardPart: string;
}

const TEAM_FACTS: Record<string, DemoTeamFacts> = {
  meridian_ed_showcase: {
    hours: "Day sessions and night call, every day",
    hardPart: "Every night call moves the next day for someone else.",
  },
  harbourline_security_small: {
    hours: "Around the clock, every day",
    hardPart: "The control room never closes, and the supervisor who covers the night must rest.",
  },
  harbourline_security_campus: {
    hours: "Around the clock, every day",
    hardPart: "A larger team with more posts and levels to keep fair.",
  },
  larkspur_ward7_nursing: {
    hours: "Day shift and night shift, every day",
    hardPart: "Every shift needs the right mix of levels, and each night needs a rest day after.",
  },
  airline_cabin_crew_base: {
    hours: "Flights every day, long-haul on set days",
    hardPart: "Each London flight needs the right mix of grades, and its crew rest the next day.",
  },
  northgate_packaging_line2: {
    hours: "Morning, afternoon and night, every day",
    hardPart:
      "Each shift needs a lead, a technician and a licensed driver, with a rest day after nights.",
  },
  saffron_lane_kitchen: {
    hours: "Lunch and dinner, Tuesday to Sunday",
    hardPart: "Every service needs a senior on the pass, and doubles must stay fair.",
  },
  bayview_family_clinics: {
    hours: "Weekday mornings and afternoons, and Saturday mornings",
    hardPart: "Every session needs a doctor and a nurse, and part-time staff work set days only.",
  },
  kallang_contact_centre: {
    hours: "Early, day and late shifts, every day",
    hardPart: "Every shift needs a team lead, and only trained agents can take the billing queue.",
  },
};

export function industryOf(card: DemoScenarioCard): string | undefined {
  return card.industry ?? TEAM_INDUSTRY[card.name];
}

export function teamFacts(card: DemoScenarioCard): DemoTeamFacts {
  return (
    TEAM_FACTS[card.name] ?? {
      hours: "Set by the team",
      hardPart: card.considerations[0]?.text ?? card.problem,
    }
  );
}

export function findIndustry(id: string | undefined): DemoIndustry | undefined {
  if (id && HIDDEN_INDUSTRIES.has(id)) return undefined;
  return DEMO_INDUSTRIES.find((industry) => industry.id === id);
}

export function teamsOf(industryId: string, cards: DemoScenarioCard[]): DemoScenarioCard[] {
  return cards.filter((card) => industryOf(card) === industryId);
}

/** The industries the picker shows, in order, with their teams. */
export function visibleIndustries(
  cards: DemoScenarioCard[],
): { industry: DemoIndustry; teams: DemoScenarioCard[] }[] {
  return DEMO_INDUSTRIES.filter((industry) => !HIDDEN_INDUSTRIES.has(industry.id))
    .map((industry) => ({
      industry,
      teams: teamsOf(industry.id, cards),
    }))
    .filter((entry) => showEmptyIndustries || entry.teams.length > 0);
}
